import type { AuthorStats, Course } from '@/data/types';
import { courseCatalog, STAT_LABEL } from '@/data/courseCatalog';

export const SKRIVELAB_MIN_ORD = 50;
export const SKRIVELAB_MAX_ORD = 200;
export const SKRIVELAB_OMDOMME_BELONNING = 5;
export const SKRIVELAB_FLAGG = 'skrivelab_brukt_v1';

export const SKRIVELAB_MODE: 'mock' | 'api' = 'mock';

export type SkrivelabAnalyse = {
  stats: AuthorStats;
  tilbakemelding: string;
  anbefaltKurs: Course | null;
  ordtelling: number;
  sterkeste: keyof AuthorStats;
  svakeste: keyof AuthorStats;
  fokusStat: keyof AuthorStats;
  fokusScore: number;
};

export type SkrivelabFeil =
  | { type: 'for-kort'; ordtelling: number }
  | { type: 'for-lang'; ordtelling: number }
  | { type: 'tom' };

export type SkrivelabResultat =
  | { ok: true; analyse: SkrivelabAnalyse }
  | { ok: false; feil: SkrivelabFeil };

const STAT_KEYS: ReadonlyArray<keyof AuthorStats> = [
  'dialog',
  'plot',
  'karakter',
  'spraak',
  'voice',
  'autentisitet',
  'disiplin',
];

const HANDLINGS_VERB = new Set([
  'gikk', 'kom', 'sa', 'tok', 'fant', 'lo', 'gråt', 'graat', 'reiste', 'satte',
  'la', 'rev', 'kastet', 'løp', 'lop', 'ropte', 'slo', 'falt', 'snudde',
  'lukket', 'åpnet', 'aapnet', 'gav', 'ga', 'dro', 'forsvant', 'kjørte', 'kjorte',
  'svarte', 'spurte', 'ringte', 'banket', 'lente', 'reiste', 'satt', 'sto',
  'stoppet', 'startet', 'kjente', 'merket',
]);

const SVAK_VERB = new Set(['sa', 'sier', 'sa,', 'sa.', 'sa?', 'sa!']);

const KREATIVE_VERB = new Set([
  'hvisket', 'mumlet', 'fnyste', 'pustet', 'sukket', 'stønnet', 'stonnet',
  'knurret', 'lo', 'kremtet', 'hostet', 'svelget', 'kvitret',
  'tordnet', 'brølte', 'brolte', 'ropte', 'skrek', 'klaget', 'jamret',
  'snerret', 'flirte', 'hvinte',
]);

const PRONOMEN = new Set([
  'hun', 'han', 'de', 'dem', 'henne', 'ham', 'haen', 'seg', 'hans', 'hennes',
  'deres', 'sin', 'sitt', 'sine',
]);

const SANSE_ORD = new Set([
  'lukt', 'lukten', 'duft', 'duften', 'stank', 'stanken',
  'lyd', 'lyden', 'støy', 'stoy', 'klang', 'klangen',
  'syn', 'synet', 'glimt', 'skygge', 'skyggen',
  'smak', 'smaken', 'sødt', 'sodt', 'bittert', 'salt',
  'kald', 'kaldt', 'varm', 'varmt', 'hud', 'huden',
  'kjølig', 'kjolig', 'hett', 'rått', 'raatt',
  'røyk', 'royk', 'svette', 'pust',
]);

const VANLIGE_FEIL = [
  /\bog så\b/gi,
  /\bda jeg vente\b/gi,
  /\bhvis jeg ville hadde\b/gi,
  /\bav og\b/gi,
  /\bhva\?+/g,
];

export function tellOrd(tekst: string): number {
  const trimmet = tekst.trim();
  if (!trimmet) return 0;
  const ord = trimmet.split(/\s+/).filter((o) => /\p{L}/u.test(o));
  return ord.length;
}

export function analyserTekst(
  tekst: string,
  rng: () => number = Math.random,
): SkrivelabResultat {
  const trimmet = tekst.trim();
  if (trimmet.length === 0) return { ok: false, feil: { type: 'tom' } };

  const ordtelling = tellOrd(trimmet);
  if (ordtelling < SKRIVELAB_MIN_ORD) {
    return { ok: false, feil: { type: 'for-kort', ordtelling } };
  }
  if (ordtelling > SKRIVELAB_MAX_ORD) {
    return { ok: false, feil: { type: 'for-lang', ordtelling } };
  }

  const stats = beregnStats(trimmet, rng);
  const { sterkeste, svakeste } = finnEkstremer(stats);
  const tilbakemelding = genererTilbakemelding(stats, sterkeste, svakeste, rng);
  const anbefaltKurs = finnAnbefaltKurs(stats);
  const fokusStat = sterkeste;
  const fokusScore = stats[sterkeste];

  return {
    ok: true,
    analyse: {
      stats,
      tilbakemelding,
      anbefaltKurs,
      ordtelling,
      sterkeste,
      svakeste,
      fokusStat,
      fokusScore,
    },
  };
}

function beregnStats(tekst: string, rng: () => number): AuthorStats {
  const norm = tekst.toLowerCase();
  const ord = norm.split(/\s+/).filter((o) => o.length > 0);
  const totale = Math.max(1, ord.length);
  const setninger = tekst
    .split(/[.!?\n]+/)
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
  const antallSetninger = Math.max(1, setninger.length);
  const setningslengder = setninger.map(
    (s) => s.split(/\s+/).filter((o) => o.length > 0).length,
  );

  const dialogScore = beregnDialog(tekst);
  const plotScore = beregnPlot(ord);
  const karakterScore = beregnKarakter(tekst, ord);
  const spraakScore = beregnSpraak(ord, totale, setningslengder);
  const voiceScore = beregnVoice(tekst, ord);
  const autentisitetScore = beregnAutentisitet(tekst, ord);
  const disiplinScore = beregnDisiplin(setningslengder, antallSetninger, tekst);

  return {
    dialog: jitter(klemTilOmraade(dialogScore, 30, 90), rng),
    plot: jitter(klemTilOmraade(plotScore, 30, 90), rng),
    karakter: jitter(klemTilOmraade(karakterScore, 30, 90), rng),
    spraak: jitter(klemTilOmraade(spraakScore, 30, 90), rng),
    voice: jitter(klemTilOmraade(voiceScore, 30, 90), rng),
    autentisitet: jitter(klemTilOmraade(autentisitetScore, 30, 90), rng),
    disiplin: jitter(klemTilOmraade(disiplinScore, 30, 90), rng),
  };
}

function beregnDialog(tekst: string): number {
  const anforsel = (tekst.match(/[«»"'"'']/g) ?? []).length;
  const sporsmaal = (tekst.match(/\?/g) ?? []).length;
  const tankestrek = (tekst.match(/—|–/g) ?? []).length;
  const score = anforsel * 8 + sporsmaal * 6 + tankestrek * 4;
  return 35 + score;
}

function beregnPlot(ord: string[]): number {
  let handlinger = 0;
  for (const o of ord) {
    const rent = o.replace(/[^\p{L}]/gu, '');
    if (HANDLINGS_VERB.has(rent)) handlinger += 1;
  }
  const ratio = handlinger / Math.max(1, ord.length);
  return 35 + Math.round(ratio * 600);
}

function beregnKarakter(tekst: string, ord: string[]): number {
  const navnKandidater = (tekst.match(/\b[A-ZÆØÅ][a-zæøå]{2,}/g) ?? []).filter(
    (n, i, arr) => arr.indexOf(n) === i,
  );
  let pronomen = 0;
  for (const o of ord) {
    const rent = o.replace(/[^\p{L}]/gu, '');
    if (PRONOMEN.has(rent)) pronomen += 1;
  }
  const navnScore = Math.min(40, navnKandidater.length * 12);
  const pronomenScore = Math.min(30, Math.round((pronomen / Math.max(1, ord.length)) * 300));
  return 30 + navnScore + pronomenScore;
}

function beregnSpraak(
  ord: string[],
  totale: number,
  setningslengder: number[],
): number {
  const unike = new Set(
    ord
      .map((o) => o.replace(/[^\p{L}\p{N}]/gu, ''))
      .filter(Boolean),
  );
  const mangfold = unike.size / Math.max(1, totale);
  const snittSetning =
    setningslengder.reduce((a, b) => a + b, 0) / Math.max(1, setningslengder.length);
  const lengdeScore = snittSetning > 4 && snittSetning < 22 ? 30 : 15;
  return 30 + Math.round(mangfold * 60) + lengdeScore;
}

function beregnVoice(tekst: string, ord: string[]): number {
  let kreative = 0;
  let svake = 0;
  for (const o of ord) {
    const rent = o.replace(/[^\p{L}]/gu, '');
    if (KREATIVE_VERB.has(rent)) kreative += 1;
    if (SVAK_VERB.has(rent)) svake += 1;
  }
  const eksklamasjon = (tekst.match(/!/g) ?? []).length;
  return 40 + kreative * 10 + eksklamasjon * 3 - svake * 4;
}

function beregnAutentisitet(tekst: string, ord: string[]): number {
  let sanser = 0;
  for (const o of ord) {
    const rent = o.replace(/[^\p{L}]/gu, '');
    if (SANSE_ORD.has(rent)) sanser += 1;
  }
  const konkreteSubstantiv = (tekst.match(/\b(bord|stol|vindu|dør|dor|kaffe|sigarett|regn|sno|sne|gate|hus|leilighet|bilen)\b/gi) ?? [])
    .length;
  return 35 + sanser * 6 + konkreteSubstantiv * 4;
}

function beregnDisiplin(setningslengder: number[], antallSetninger: number, tekst: string): number {
  if (setningslengder.length === 0) return 50;
  const snitt = setningslengder.reduce((a, b) => a + b, 0) / setningslengder.length;
  const varians =
    setningslengder.reduce((sum, l) => sum + (l - snitt) ** 2, 0) / setningslengder.length;
  const variasjon = Math.min(40, Math.round(varians * 1.5));
  let feil = 0;
  for (const m of VANLIGE_FEIL) {
    feil += (tekst.match(m) ?? []).length;
  }
  return 40 + variasjon + Math.min(10, antallSetninger * 2) - feil * 6;
}

function klemTilOmraade(v: number, lav: number, hoy: number): number {
  if (Number.isNaN(v) || !Number.isFinite(v)) return lav;
  return Math.max(lav, Math.min(hoy, Math.round(v)));
}

function jitter(v: number, rng: () => number): number {
  const offset = Math.round((rng() - 0.5) * 20);
  const ut = v + offset;
  return Math.max(0, Math.min(100, ut));
}

function finnEkstremer(stats: AuthorStats): {
  sterkeste: keyof AuthorStats;
  svakeste: keyof AuthorStats;
} {
  let sterkeste: keyof AuthorStats = 'dialog';
  let svakeste: keyof AuthorStats = 'dialog';
  let maks = -Infinity;
  let min = Infinity;
  for (const k of STAT_KEYS) {
    const v = stats[k];
    if (v > maks) {
      maks = v;
      sterkeste = k;
    }
    if (v < min) {
      min = v;
      svakeste = k;
    }
  }
  return { sterkeste, svakeste };
}

const FRASER: Record<keyof AuthorStats, { hoy: string[]; lav: string[] }> = {
  dialog: {
    hoy: [
      'Replikkene drar leseren med — du har øre for hvordan folk faktisk snakker.',
      'Dialogen har rytme og pust, som om personene satt rett ved siden av oss.',
      'Du veksler fint mellom kort og lengre replikker — det gir liv.',
      'Tonen i samtalene er sikker og varm.',
    ],
    lav: [
      'Mer dialog kan gi liv til scenene — la personene få snakke.',
      'Karakterene snakker for sjelden — leseren får ikke høre stemmene deres.',
      'Vurder å bytte noen beskrivelser ut med en velplassert replikk.',
      'Dialog kan løfte teksten ut av det refererende.',
    ],
  },
  plot: {
    hoy: [
      'Du har klart driv i fortellingen — noe skjer hele tiden.',
      'Bevegelsen i teksten holder leseren til.',
      'Det er en tydelig retning her — flott momentum.',
      'Du våger å la handling fortelle, ikke bare beskrivelser.',
    ],
    lav: [
      'Vurder å bygge mer momentum — la noe skje tidlig.',
      'Spenningen kan strammes opp med et tydeligere drag fremover.',
      'Litt mer handling vil løfte den indre stillstanden.',
      'Tenk på hva karakteren faktisk gjør — ikke bare tenker.',
    ],
  },
  karakter: {
    hoy: [
      'Karakterene står skarpt — vi ser dem.',
      'Du gir personene din indre liv som leseren kan kjenne på.',
      'Det er troverdig psykologi i hvordan figurene reagerer.',
      'Personene er ikke bare navn — de er mennesker.',
    ],
    lav: [
      'Karakterene blir litt blasse — gi dem en konkret detalj som skiller dem ut.',
      'Vi savner et indre liv hos personene — hva ønsker de egentlig?',
      'Navn alene er ikke nok — vis oss hvem de er.',
      'En karakteristisk vane eller frase kan gi figuren liv.',
    ],
  },
  spraak: {
    hoy: [
      'Setningene er stødige og varierte — språket bærer.',
      'Du har et rikt vokabular uten å virke anstrengt.',
      'Rytmen i prosaen sitter godt.',
      'Du veksler elegant mellom lange og korte setninger.',
    ],
    lav: [
      'Vokabularet kan utvides — for mange ord gjentas.',
      'Setningsstrukturen blir litt monoton — varier mer.',
      'Prøv å luke ut fyllord og gjentakelser.',
      'Et bredere ordforråd vil gi prosaen mer farge.',
    ],
  },
  voice: {
    hoy: [
      'Du har en stemme — den er din alene.',
      'Tonen er gjenkjennelig allerede etter to setninger.',
      'Det er en personlighet i teksten som løfter den.',
      'Du våger å være deg selv i prosaen.',
    ],
    lav: [
      'Stemmen din kan komme tydeligere frem — våg å være personlig.',
      'Teksten er litt anonym — finn det som bare du kan skrive.',
      'Bytt ut «sa» med et verb som er mer ditt.',
      'Hva er det bare du kan si om denne scenen? Skriv det.',
    ],
  },
  autentisitet: {
    hoy: [
      'Du forankrer scenen i sansene — lukt, lyd, lys.',
      'Konkrete detaljer gjør teksten ekte.',
      'Du tror på det du skriver, og det smitter.',
      'Det er en kropp i denne prosaen — fint.',
    ],
    lav: [
      'Mer sansing vil forankre scenen — hva luktet det, hva hørte du?',
      'Vær konkret — bord, kaffe, regn. Ting som veier noe.',
      'Teksten svever litt — la den lande i en kropp.',
      'En enkelt sanseopplevelse kan gjøre hele scenen levende.',
    ],
  },
  disiplin: {
    hoy: [
      'Setningene er rene og kontrollerte — du har redigert godt.',
      'Du varierer rytme uten å miste taket.',
      'Prosaen er disiplinert uten å være stiv.',
      'Du vet når du skal trekke pusten — det merkes.',
    ],
    lav: [
      'Vask gjennom — det er småfeil og uvariert rytme.',
      'Setningene er for like i lengde — variér.',
      'Les høyt før neste utkast — du vil høre hva som halter.',
      'En grundig korrektur vil løfte teksten et hakk.',
    ],
  },
};

const AVSLUTNINGER = [
  'Fortsett å skrive — det er noe her.',
  'Du er på vei — bli ved skrivebordet.',
  'Skriv videre, og send oss neste utkast.',
  'Det er et fundament her vi kan bygge på.',
  'Du har noe — pleie det.',
];

export function genererTilbakemelding(
  _stats: AuthorStats,
  sterkeste: keyof AuthorStats,
  svakeste: keyof AuthorStats,
  rng: () => number = Math.random,
): string {
  const sterkLabel = STAT_LABEL[sterkeste];
  const svakLabel = STAT_LABEL[svakeste];
  const sterkFraser = FRASER[sterkeste].hoy;
  const svakFraser = FRASER[svakeste].lav;
  const sterkFrase = velgTilfeldig(sterkFraser, rng);
  const svakFrase = velgTilfeldig(svakFraser, rng);
  const avslutning = velgTilfeldig(AVSLUTNINGER, rng);

  return [
    `Sterkest: ${sterkLabel}. ${sterkFrase}`,
    `Mulighet: ${svakLabel}. ${svakFrase}`,
    avslutning,
  ].join(' ');
}

function velgTilfeldig<T>(liste: ReadonlyArray<T>, rng: () => number): T {
  if (liste.length === 0) throw new Error('Tom fraseliste');
  const i = Math.floor(rng() * liste.length);
  return liste[Math.min(liste.length - 1, Math.max(0, i))];
}

function finnAnbefaltKurs(stats: AuthorStats): Course | null {
  if (!courseCatalog.isReady) return null;
  const alleHoye = STAT_KEYS.every((k) => stats[k] >= 75);
  if (alleHoye) return null;
  const treff = courseCatalog.forSlott(stats, 1);
  return treff.length > 0 ? treff[0] : null;
}
